import { MessageSquare, Inbox, MessageCircle } from 'lucide-react';
import { usePancakeStore } from '../../store/pancake.store';
import { platformBadge } from './PancakeDashboardFilters';

export default function PancakeOverview({ isDark }: { isDark: boolean }) {
  const { overview } = usePancakeStore();

  if (!overview) return null;

  const scorecards = [
    { label: 'Tổng hội thoại', value: overview.totalConversations, icon: MessageSquare, color: 'cyan' },
    { label: 'Inbox', value: overview.totalInbox, icon: Inbox, color: 'blue' },
    { label: 'Comment', value: overview.totalComment, icon: MessageCircle, color: 'green' },
  ];

  const colorMap: Record<string, string> = {
    cyan: isDark ? 'text-cyan-400' : 'text-cyan-600',
    blue: isDark ? 'text-blue-400' : 'text-blue-600',
    green: isDark ? 'text-green-400' : 'text-green-600',
  };

  const bgMap: Record<string, string> = {
    cyan: isDark ? 'bg-cyan-500/10' : 'bg-cyan-50',
    blue: isDark ? 'bg-blue-500/10' : 'bg-blue-50',
    green: isDark ? 'bg-green-500/10' : 'bg-green-50',
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Scorecards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {scorecards.map((card) => {
          const Icon = card.icon;
          return (
            <div
              key={card.label}
              className={`rounded-lg p-4 sm:p-5 border ${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}`}
            >
              <div className="flex items-center gap-3">
                <div className={`p-2.5 rounded-lg ${bgMap[card.color]}`}>
                  <Icon className={`w-5 h-5 ${colorMap[card.color]}`} />
                </div>
                <div>
                  <p className={`text-xs uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{card.label}</p>
                  <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {card.value.toLocaleString('en-US')}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Page breakdown table */}
      <div className={`rounded-lg border overflow-hidden ${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}`}>
        <div className={`px-4 py-3 border-b ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
          <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Chi tiết theo trang</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className={isDark ? 'bg-slate-800' : 'bg-gray-50'}>
                <th className={`text-left px-4 py-3 font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Trang</th>
                <th className={`text-left px-4 py-3 font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Nền tảng</th>
                <th className={`text-right px-4 py-3 font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Hội thoại</th>
                <th className={`text-right px-4 py-3 font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Inbox</th>
                <th className={`text-right px-4 py-3 font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Comment</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-slate-700' : 'divide-gray-100'}`}>
              {overview.pageBreakdown.map((row) => (
                <tr key={row.pageId} className={`${isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-gray-50'} transition-colors`}>
                  <td className={`px-4 py-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>{row.pageName}</td>
                  <td className="px-4 py-3">{platformBadge(row.platform)}</td>
                  <td className={`text-right px-4 py-3 font-medium ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`}>
                    {row.conversationCount.toLocaleString('en-US')}
                  </td>
                  <td className={`text-right px-4 py-3 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                    {row.inboxCount.toLocaleString('en-US')}
                  </td>
                  <td className={`text-right px-4 py-3 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                    {row.commentCount.toLocaleString('en-US')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
